import { Modal } from '@cimpress/react-components'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import * as uuid from 'uuid'
import { CarrierSelection } from '../../../../../carrier-configuration/components/add-carrier-flow/carrierAccountData'
import { SnackbarController } from '../../../../../common/components/SnackbarController'
import { Location } from '../../../../../common/models'
import { EnhancedCaasProfile } from '../../../../../common/models-carriers'
import {
  updatePickupCalendar,
  filterCarrierServicesForCalendar,
  addCalendarToMapping,
} from '../../../../../common/proxy/calendars-store'
import { useLogisticsLocation } from '../../../../../locations/LocationContext'
import { EditCalendar } from '../../../../models'
import useWeeklyCalendarContext from '../../WeeklyCalendarContext'
import { PickupCalendarEditorModal } from './PickupCalendarEditorModal'

interface Props {
  pickupCalendarId: string
  carrierServicesToCalendarNames: { [key: string]: string }
  caasProfiles: EnhancedCaasProfile[]
  onClose: () => void
}

export default function PickupCalendarEditModalContainer(props: Props) {
  const { t } = useTranslation()
  const context = useWeeklyCalendarContext()
  const { logisticsLocation, updateLocation } = useLogisticsLocation()

  const calendar = context.pickupCalendarDaysWithLegend.find(
    pc => pc.pickupCalendarDisplay.calendar.id === props.pickupCalendarId
  )

  const pickupCalendar = calendar!.pickupCalendarDisplay.calendar

  const carrierTypesReverseMap = new Map<string, Set<EnhancedCaasProfile>>()
  for (const profile of props.caasProfiles) {
    for (const caas of profile.carrierServices) {
      const profiles =
        carrierTypesReverseMap.get(caas.key) || new Set<EnhancedCaasProfile>()
      profiles.add(profile)

      carrierTypesReverseMap.set(caas.key, profiles)
    }
  }
  const [saving, setSaving] = React.useState(false)

  const carrierAccountData = getCarrierAccountData(
    logisticsLocation,
    props.pickupCalendarId,
    carrierTypesReverseMap
  )

  if (carrierAccountData === undefined) {
    return (
      <Modal
        show={true}
        onRequestHide={props.onClose}
        closeButton={true}
        closeOnOutsideClick={false}
        title={t('calendars.weekly.weeklyCalendarContainer.errorLoadingTitle')}
        style={{ width: '526px' }}
      >
        <div>
          <span>
            {t('calendars.weekly.weeklyCalendarContainer.errorLoadingComment') +
              ' '}
          </span>
          <a href="mailto:logisticssupport@cimpress.com">
            logisticssupport@cimpress.com
          </a>
        </div>
      </Modal>
    )
  }

  const save = async (data: EditCalendar) => {
    setSaving(true)
    try {
      const carrierServices = data.carrierSelection.carrierServices
      const correlationId = uuid.v4()

      if (data.calendarName !== pickupCalendar.name) {
        await updatePickupCalendar(
          pickupCalendar.id!,
          pickupCalendar.etag!,
          [
            {
              op: 'replace',
              path: '/name',
              value: data.calendarName,
            },
          ],
          correlationId
        )
      }
      await updateLocation(locationToUpdate => {
        filterCarrierServicesForCalendar(
          locationToUpdate.pickupCalendars.mapping,
          props.pickupCalendarId,
          carrierServices!
        )
        addCalendarToMapping(
          locationToUpdate.pickupCalendars.mapping,
          props.pickupCalendarId,
          carrierServices!
        )
      })
      await context.loadCalendarData()
      SnackbarController.show(
        t('calendars.weekly.pickupCalendarsLegend.pickupCalendarUpdated', {
          name: data.calendarName,
        }),
        'success'
      )

      setSaving(false)
      props.onClose()
    } catch (e) {
      SnackbarController.show(
        t(
          'calendars.weekly.pickupCalendarsLegend.updatingPickupCalendarFailed',
          { name: pickupCalendar.name }
        ),
        'danger'
      )
    }
  }

  return (
    <PickupCalendarEditorModal
      hasComplexSetup={hasComplexCalendarSetup(
        logisticsLocation,
        carrierAccountData
      )}
      pickupCalendar={pickupCalendar}
      saving={saving}
      onClose={props.onClose}
      onSave={save}
      carrierAccountTypes={props.caasProfiles}
      carrierAccountData={carrierAccountData}
      carrierServicesToCalendarNames={props.carrierServicesToCalendarNames}
    />
  )
}

function getCarrierAccountData(
  location: Location,
  pickupCalendarId: string,
  carrierTypesReverseMap: Map<string, Set<EnhancedCaasProfile>>
): CarrierSelection | undefined {
  const carrierServices = []
  for (const csKey of Object.keys(location.pickupCalendars.mapping)) {
    const calendarMapping = location.pickupCalendars.mapping[csKey]
    if (
      calendarMapping.id === pickupCalendarId ||
      (calendarMapping.additionalCalendars &&
        calendarMapping.additionalCalendars.some(
          cal => cal.id === pickupCalendarId
        ))
    ) {
      carrierServices.push(csKey)
    }
  }

  const profilesSets = carrierServices.map(
    cs => carrierTypesReverseMap.get(cs) || new Set<EnhancedCaasProfile>()
  )
  if (profilesSets.length === 0) {
    return undefined
  }
  const possibleProfiles = profilesSets.reduce((acc, curr) =>
    intersection(acc, curr)
  )
  const carrier = [...possibleProfiles.values()][0]

  return {
    carrier: carrier ? carrier.key : '',
    carrierServices,
  }
}

function intersection<T>(setA: Set<T>, setB: Set<T>): Set<T> {
  const result = new Set<T>()
  for (const elem of setB) {
    if (setA.has(elem)) {
      result.add(elem)
    }
  }

  return result
}

function hasComplexCalendarSetup(
  location: Location,
  carrierSelection: CarrierSelection
): boolean {
  if (carrierSelection.carrierServices === undefined) {
    return false
  }

  return carrierSelection.carrierServices.reduce((isComplex, service) => {
    const mapping = location.pickupCalendars.mapping[service]

    return (
      isComplex ||
      (mapping !== undefined &&
        mapping.additionalCalendars !== undefined &&
        mapping.additionalCalendars.length > 0)
    )
  }, false)
}
